import type { ComponentInit } from "./registry";

const LINE = "fa-underline-line";
const SPLIT = "fa-underline-split";
const INDEX = "--fa-underline-reveal-index";
const PROBE = "data-fa-line-probe";

/* Only a lone text node: on the vendor's page this is their markup, and rebuilding from
   textContent would delete an authored <em> with nothing to report it. */
const splittable = (element: HTMLElement): string | null => {
	const [child] = element.childNodes;

	return element.childNodes.length === 1 && child instanceof Text ? child.data : null;
};

/* One probe span per word, measured by `offsetTop`: a word that starts lower than the one
   before it opened a new line box, which is the only way to read where the browser wrapped. */
const measure = (element: HTMLElement, text: string): string[] => {
	const words = text.split(/\s+/).filter(Boolean);

	element.replaceChildren(
		...words.map((word, index) => {
			const probe = document.createElement("span");

			probe.setAttribute(PROBE, "");
			probe.textContent = index === words.length - 1 ? word : `${word} `;

			return probe;
		}),
	);

	const lines: string[][] = [];
	let top: number | null = null;

	for (const [index, probe] of [
		...element.querySelectorAll<HTMLElement>(`[${PROBE}]`),
	].entries()) {
		if (top === null || Math.abs(probe.offsetTop - top) > 1) {
			top = probe.offsetTop;
			lines.push([]);
		}

		lines[lines.length - 1]?.push(words[index] ?? "");
	}

	return lines.map((line) => line.join(" "));
};

const paint = (element: HTMLElement, lines: readonly string[]): void => {
	element.replaceChildren(
		...lines.flatMap((line, index) => {
			const span = document.createElement("span");

			span.className = LINE;
			span.style.setProperty(INDEX, String(index));
			span.textContent = line;

			return index === 0 ? [span] : [document.createTextNode(" "), span];
		}),
	);

	element.classList.add(SPLIT);
};

export const initLineSplit: ComponentInit = (element) => {
	const text = splittable(element);

	if (text === null) return;

	let width = element.offsetWidth;

	const rebuild = () => {
		paint(element, measure(element, text));
	};

	rebuild();

	/* Width, not any resize: the line breaks only move when the measure does, and a height
	   change is this element's own reaction to the rebuild that just ran. */
	const observer = new ResizeObserver(() => {
		if (Math.abs(element.offsetWidth - width) <= 1) return;

		width = element.offsetWidth;
		rebuild();
	});

	observer.observe(element);

	return () => {
		observer.disconnect();
		element.classList.remove(SPLIT);
		element.replaceChildren(text);
	};
};
